import { HOMApprovalItem } from "../types/hom-approval";
import { Pagination } from "../types/pagination";
import { IHOMApprovalService } from "../types/services/hom-approval-service";
import { FetchResult, GET, PATCH } from "../utils/fetch";

class HOMApprovalService implements IHOMApprovalService {
    async getApprovalRequests(
        status?: string[],
        approver_id?: string,
        location_id?: string,
        query?: string,
        order_by?: string,
        page?: string,
        per_page?: string,
        ticket_status?: string[],
    ): Promise<FetchResult<HOMApprovalItem[]>> {
        const qp = new URLSearchParams();

        if (status && status.length > 0) {
            status.forEach((s) => {
                qp.append("status[]", s);
            });
        }

        if (ticket_status && ticket_status.length > 0) {
            ticket_status.forEach((s) => {
                qp.append("ticket_status[]", s);
            });
        }

        if (approver_id) {
            qp.set("approver_id", approver_id);
        }

        if (location_id) {
            qp.set("location_id", location_id);
        }

        if (query) {
            qp.set("query", query);
        }

        if (order_by) {
            qp.set("order_by", order_by);
        }

        if (page) {
            qp.set("page", page);
        }

        if (per_page) {
            qp.set("per_page", per_page);
        }

        const marshalled = qp.toString();

        let url = "/api/v2/hom_approvals";

        if (marshalled) {
            url = `${url}?${marshalled}`;
        }

        const { error, response } = await GET<ApprovalsListResponse>(url);

        if (error) {
            return {
                error,
                response: null,
            };
        }

        if (!response) {
            return {
                error: new Error("Unknown Error"),
                response: null,
            };
        }

        const approvals = response.approvals?.map((each: any) => {
            const {
                id,
                ticket_title,
                villa_name,
                ops_poc,
                assigned_on,
                requested_budget,
                approved_budget,
                quotation,
                status,
                action_status,
                status_of_request,
                approver_name,
                rejected_by_name,
                pending_at_name,
                can_approve,
                can_object,
                can_update,
                ticket_slug,
            } = each;

            const quotationItem = quotation && Array.isArray(quotation) && quotation.length > 0
                ? quotation[0]
                : null;
            const quotation_url = quotationItem?.link;
            const quotation_filename = quotation_url
                ? quotation_url.split("/").pop()?.split("?")[0] || "View Document"
                : undefined;

            const approval: HOMApprovalItem = {
                id: id,
                ticket_title: ticket_title || "",
                villa_name: villa_name || "",
                ops_poc: ops_poc || "",
                assigned_on: assigned_on || "",
                requested_budget: requested_budget ?? 0,
                approved_budget: approved_budget ?? 0,
                quotation: quotation || [],
                quotation_url: quotation_url,
                quotation_filename: quotation_filename,
                action_status: action_status,
                status_of_request: status_of_request,
                status: status || action_status || status_of_request || "",
                approver_name: approver_name,
                rejected_by_name: rejected_by_name,
                pending_at_name: pending_at_name,
                can_approve: can_approve ?? false,
                can_object: can_object ?? false,
                can_update: can_update ?? false,
                ticket_slug: ticket_slug,
            };

            return approval;
        }) || [];

        return {
            error: null,
            paginate: response.paginate,
            response: approvals,
        };
    }

    async updateStatus(
        id: number,
        status: "approved" | "rejected",
        comment?: string,
    ): Promise<FetchResult<void>> {
        const url = `/api/v2/hom_approvals/${id}/update_status`,
            { error } = await PATCH<{ success: boolean }>(url, {
                status: status,
                comment: comment || "",
            });

        if (error) {
            return {
                error,
                response: null,
            };
        }

        return {
            error: null,
            response: null,
        };
    }
}

export default HOMApprovalService;

type ApprovalsListResponse = {
    approvals?: any[];
    paginate: Pagination;
};
